import CachedIcon from "@mui/icons-material/Cached";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { currentMonthRange, describeAge, describeRange, isFullMonthRange, stepRange } from "./pairingStatusSelectors";
import { IEpochRange, IFlightPairingStatusMeta } from "./pairingStatusTypes";

export type PairingStatusView = "calendar" | "list";

/** Largest range the window will request (matches the backend's on-demand compute limit). */
export const MAX_RANGE_DAYS = 124;
const MS_PER_DAY = 86_400_000;

interface IPairingStatusHeaderProps {
  range: IEpochRange;
  onRangeChange: (range: IEpochRange) => void;
  view: PairingStatusView;
  onViewChange: (view: PairingStatusView) => void;
  meta: IFlightPairingStatusMeta | null;
  /** The evaluation the stale warning judges (see `oldestScheduledEvaluation`). */
  staleCheckOldest: number | null;
  loadedAt: number | null;
  lastUpdateAt: number | null;
  isLoading: boolean;
  isRecomputing: boolean;
  onRecompute: () => void;
  onReload: () => void;
  flightCount: number;
  routeCount: number;
}

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px 2px 8px",
  "& .page_header": { margin: 0, whiteSpace: "nowrap" },
  "& .subheader": { margin: "0px 8px", color: theme.custom.forms.labels, fontWeight: 400 },
  "& .range_controls": { display: "flex", flexDirection: "row", alignItems: "center", gap: 2 },
  "& .range_datetimefield": {
    width: "auto",
    "& .input_control label": { width: "auto" },
    "& .input_control .react-datepicker-wrapper": { margin: 0, "& input": { width: 90 } },
  },
  "& .range_note": { marginLeft: 6, color: theme.custom.forms.labels, whiteSpace: "nowrap" },
  "& .freshness": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: "auto",
    color: theme.custom.forms.labels,
    fontSize: 11,
    whiteSpace: "nowrap",
  },
  "& .freshness.stale": { color: theme.palette.warning.main },
  "& .refresh_btn": { padding: "2px 8px", height: 22, fontSize: 10 },
  "& .MuiToggleButton-root": { padding: "2px 6px" },
}));

/** Re-render periodically so "x min ago" stays honest without any data changing. */
const useClockTick = (intervalMs: number): number => {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
};

/**
 * The oldest evaluation on the days the scheduled refresh keeps fresh is called out when older than this. The refresh
 * re-evaluates its window every 15 minutes by default, so this only trips when the schedule is not running. Days
 * outside the window are re-evaluated only on changes and never trip it.
 */
const STALE_AFTER_MS = 60 * 60 * 1000;

export const PairingStatusHeader = (props: IPairingStatusHeaderProps): JSX.Element => {
  const {
    range,
    onRangeChange,
    view,
    onViewChange,
    meta,
    staleCheckOldest,
    loadedAt,
    lastUpdateAt,
    isLoading,
    isRecomputing,
    onRecompute,
    onReload,
    flightCount,
    routeCount,
  } = props;
  const now = useClockTick(30_000);

  const newestComputedAt = Math.max(meta?.lastComputedAt ?? 0, lastUpdateAt ?? 0) || null;
  const oldestComputedAt = meta?.oldestComputedAt ?? newestComputedAt;
  const hasFlights = (meta?.count ?? 0) > 0 || flightCount > 0;
  const isStale = hasFlights && !!staleCheckOldest && now - staleCheckOldest > STALE_AFTER_MS;
  const scheduledWindow = meta?.scheduledWindow;
  const rangeDays = Math.round((range.end + 1 - range.start) / MS_PER_DAY);
  const [clampedNote, setClampedNote] = useState<string | null>(null);

  const applyRange = (next: IEpochRange, clamped: boolean): void => {
    setClampedNote(clamped ? `Limited to ${MAX_RANGE_DAYS} days` : null);
    onRangeChange(next);
  };
  const setStart = (value: string | number): void => {
    const start = moment.utc(value).startOf("day").valueOf();
    if (!Number.isFinite(start) || start >= range.end) return;
    const maxEnd = start + MAX_RANGE_DAYS * MS_PER_DAY - 1;
    applyRange({ start, end: Math.min(range.end, maxEnd) }, range.end > maxEnd);
  };
  const setEnd = (value: string | number): void => {
    const end = moment.utc(value).endOf("day").valueOf();
    if (!Number.isFinite(end) || end <= range.start) return;
    const minStart = end - MAX_RANGE_DAYS * MS_PER_DAY + 1;
    applyRange({ start: Math.max(range.start, minStart), end }, range.start < minStart);
  };

  const freshnessText = isLoading
    ? "Loading..."
    : !hasFlights
      ? "No flights in range"
      : `Evaluated ${describeAge(newestComputedAt, now)}`;

  return (
    <Header>
      <Typography className="page_header" variant="h6" color="textPrimary">
        Flight Pairing Status v2
        <span className="subheader">({describeRange(range)})</span>
      </Typography>

      <Box className="range_controls">
        <Tooltip title={isFullMonthRange(range) ? "Previous month" : `Back ${rangeDays} days`}>
          <IconButton size="small" onClick={() => applyRange(stepRange(range, -1), false)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button size="small" variant="outlined" onClick={() => applyRange(currentMonthRange(), false)}>
          This month
        </Button>
        <Tooltip title={isFullMonthRange(range) ? "Next month" : `Forward ${rangeDays} days`}>
          <IconButton size="small" onClick={() => applyRange(stepRange(range, 1), false)}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <DateTimeField
          className="start range_datetimefield"
          name="pairing_status_start"
          label="Start"
          value={range.start}
          onChange={setStart}
          isEpoch={true}
          hideTimeControl={true}
          maxDate={range.end - MS_PER_DAY}
        />
        <DateTimeField
          className="end range_datetimefield"
          name="pairing_status_end"
          label="End"
          value={range.end}
          onChange={setEnd}
          isEpoch={true}
          hideTimeControl={true}
          minDate={range.start + MS_PER_DAY}
        />
        <Tooltip title={`UTC days. Up to ${MAX_RANGE_DAYS} days at a time.`}>
          <Typography className="range_note" variant="caption">
            {rangeDays} days · {flightCount} flights · {routeCount} routes
            {clampedNote ? ` · ${clampedNote}` : ""}
          </Typography>
        </Tooltip>
      </Box>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={view}
        onChange={(_event, next: PairingStatusView | null) => next && onViewChange(next)}
      >
        <ToggleButton value="calendar">
          <Tooltip title="Calendar: one row per flight number and routing, one column per day">
            <CalendarViewMonthIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="list">
          <Tooltip title="List: one row per flight, sorted by departure">
            <ViewListIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Box className={`freshness${isStale ? " stale" : ""}`}>
        <Tooltip
          title={
            <Box>
              <div>
                Statuses are evaluated by the server when crew, pairings or flights change
                {scheduledWindow === null ? "" : ", and on a schedule"}; changes appear here automatically.
              </div>
              <div>
                Newest evaluation in range:{" "}
                {newestComputedAt ? `${moment.utc(newestComputedAt).format("DD MMM HH:mm:ss")}z` : "-"}
              </div>
              <div>
                Oldest evaluation in range:{" "}
                {oldestComputedAt ? `${moment.utc(oldestComputedAt).format("DD MMM HH:mm:ss")}z` : "-"}
              </div>
              <div>Loaded here: {loadedAt ? `${moment.utc(loadedAt).format("DD MMM HH:mm:ss")}z` : "-"}</div>
              {scheduledWindow && (
                <div>
                  Re-evaluated on a schedule: {moment.utc(scheduledWindow.start).format("DD MMM")} to{" "}
                  {moment.utc(scheduledWindow.end).format("DD MMM")}. Other days are re-evaluated when something about
                  their flights changes.
                </div>
              )}
              {scheduledWindow === null && (
                <div>
                  No scheduled re-evaluation in this environment: flights are re-evaluated when something about them
                  changes.
                </div>
              )}
              {meta?.computedOnDemand && <div>This range was evaluated on demand when it was opened.</div>}
              {isStale && (
                <div>
                  {scheduledWindow
                    ? "Days the schedule keeps fresh have not been re-evaluated for over an hour. Use Recompute if you suspect they are out of date."
                    : "Part of this range has not been re-evaluated for over an hour. Use Recompute if you suspect it is out of date."}
                </div>
              )}
            </Box>
          }
        >
          <span>{freshnessText}</span>
        </Tooltip>
        <Tooltip title="Reload what the server has (no re-evaluation)">
          <span>
            <IconButton size="small" onClick={onReload} disabled={isLoading || isRecomputing}>
              <CachedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Re-evaluate every flight in this range on the server now">
          <span>
            <Button
              className="refresh_btn"
              id="recompute_flightpairingstatus_btn"
              size="small"
              variant="contained"
              color="primary"
              disabled={isRecomputing || isLoading}
              onClick={onRecompute}
              startIcon={
                isRecomputing ? <CircularProgress size={10} color="inherit" /> : <RefreshIcon fontSize="small" />
              }
            >
              {isRecomputing ? "Recomputing" : "Recompute"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Header>
  );
};
